<template>
	<div
		class="athlete-selector"
	>
		<div
			class="form-input dropdown"
		>
			<div
				class="select-wrapper"
				@click="toggle"
			>
				<!-- SELECTED PLAYER -->
				<SelectedItem :item="selectedOption" />
				<span
					class="caret"
				>
					<Icon
						icon-name="down-arrow"
						class="text-white"
						size="lg"
					/>
				</span>
			</div>
		</div>

		<!-- POPUP -->
		<div
			v-if="isOpen"
			class="popup"
		>
			<!-- TITLE -->
			<div class="title">
				<h2>{{ $I18n.trans('one.choose_athlete') }}</h2>
				<Icon
					icon-name="e-remove"
					size="lg"
					@click="close"
				/>
			</div>
			<!-- SEARCH INPUT -->
			<div class="search">
				<div
					class="input-wrapper"
				>
					<input
						ref="input"
						v-model="searchInput"
						:placeholder="$I18n.trans('one.search_athlete')"
					>
					<Icon
						icon-name="e-remove"
						size="lg"
						:class="`remove-icon-color ${searchInput ? '' : 'disabled'}`"
						@click="clearInput"
					/>
				</div>
			</div>
			<!-- LIST -->
			<transition name="list">
				<InfiniteScroll
					:is-last-page="true"
				>
					<ul
						ref="listContainer"
						class="dropdown-list"
					>
						<li
							v-for="option in filteredOptions"
							:key="option.key"
							class="is-small"
							:class="{'item-selected': selectedOption === option}"
							@click="handleItemClick(option)"
						>
							<ListItem
								:item="option"
								:is-selected="selectedOption === option"
							/>
						</li>
					</ul>
				</InfiniteScroll>
			</transition>
		</div>
	</div>
</template>

<script>
import Icon from '@components/Icon';
import SelectedItem from './SelectedItem.vue';
import ListItem from './ListItem.vue';
import InfiniteScroll from '@components/infinite-scroll';
import { ATHLETE_ALL_KEY, ATHLETE_UNKNOWN_ENTITY } from '@videotools/js/constants/athletes';

export default {
	name:       'MobileAthleteSelector',
	components: { Icon, SelectedItem, ListItem, InfiniteScroll },
	props:      {
		athletes: {
			type:    Array,
			default: () => [],
		},
		value: {
			default: null,
		},
		hasAllOption: {
			type: Boolean,
		},
	},
	data() {
		return {
			isOpen:      false,
			searchInput: null,
		};
	},
	computed: {
		options() {
			return [
				...(this.hasAllOption
					? [{
						label: this.allAthletesOptionLabel,
						value: ATHLETE_ALL_KEY,
					}]
					: []
				),
				{
					label: ATHLETE_UNKNOWN_ENTITY.name,
					value: ATHLETE_UNKNOWN_ENTITY,
					icon:  'users-wm',
				},
				...this.athletes.map((athlete) => ({
					id:    athlete.id,
					value: athlete,
					label: athlete.name,
					image: athlete.imageUrl,
				})),
			];
		},
		filteredOptions() {
			if (!this.searchInput) return this.options;
			return this.options.filter(option => option.value === ATHLETE_ALL_KEY || option.label.toLowerCase().includes(this.searchInput.toLowerCase()));
		},
		allAthletesOptionLabel() {
			return this.transUcfirst('one.all_athletes') + ` (${this.athletes.length})`;
		},
		selectedOption() {
			return this.options.find(option => option.value === this.value);
		},
	},
	methods: {
		clearInput() {
			if (this.searchInput) this.searchInput = null;
		},
		close() {
			this.clearInput();
			this.isOpen = false;
		},
		toggle() {
			if (this.disabled) return;
			this.isOpen = !this.isOpen;
			if (this.isOpen) {
				this.$nextTick(() => {
					this.$refs.input.focus();
				});
			}
		},
		handleItemClick(option) {
			this.$emit('input', option.value);
			this.isOpen = false;
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@shared/sass/shared-variables';

.athlete-selector {
	display: flex;
	flex-direction: column;

	.popup {
		display: flex;
		flex-direction: column;
		position: fixed;
		z-index: 9999;
		top: 0;
		left: 0;
		background-color: var(--color-grey-850);
		height: 100%;
		width: 100%;

		.title {
			display: flex;
			justify-content: space-between;
			align-items: center;
			padding-top: 24px;
			padding-bottom: 24px;
			padding-left: 16px;
			padding-right: 20px;
			border-bottom: 1px solid var(--color-grey-800);
		}

		.search {

			.input-wrapper {
				margin: 16px;
				height: 64px;
				border: 1px solid var(--color-hype-yellow);
				display: flex;
				align-items: center;
				justify-content: space-between;
				padding: 20px 12px;

				.remove-icon-color {
					color: var(--color-hype-yellow);

					&.disabled {
						color: var(--color-grey-500);
					}
				}
			}
		}

		.dropdown-list {
			flex-grow: 1;
			overflow-x: hidden;
			overflow-y: auto;
			border: 1px solid var(--color-grey-800);
			border-top: none;
			border-bottom-left-radius: 2px;
			border-bottom-right-radius: 2px;

			.item-selected {
				background-color: var(--color-grey-700);
			}

			li {
				display: flex;
				justify-content: space-between;
				align-items: center;
				padding-left: 12px;
				height: 64px;
				color: var(--color-grey-300);
			}
		}
	}

	.form-input {
		background: var(--color-grey-900);
	}

	.dropdown {
		user-select: none;
		position: relative;
		padding-top: 7px;
		padding-bottom: 7px;
		min-height: 64px;
		display: flex;
		align-items: center;

		&.is-open {
			z-index: 10000;

			.dropdown-list {
				background-color: var(--color-grey-850);
			}
		}

		.select-wrapper {
			overflow: hidden;
			display: flex;
			align-items: center;
			justify-content: space-between;
			color: var(--color-grey-600);
			min-height: 32px;
			box-sizing: border-box;
			flex: 1;

			.text-white {
				color: var(--color-white);
			}
		}
	}
}
</style>
